import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Sparkles, Loader2, RotateCw } from "lucide-react";

/**
 * Voice-to-Smart-Note textarea. Used ONLY on the agent side wherever they
 * write notes / call outcomes / follow-up text. Records mic audio, sends it
 * to Whisper, then rewrites via GPT into clean English or Hinglish (Roman).
 *
 * The agent MUST review the result — nothing is saved until the parent
 * component pulls the current value from onChange and calls its own save
 * action. This component never persists on its own.
 *
 * Props:
 *   value, onChange (string)
 *   context: 'note' | 'call_outcome' | 'followup' (passed to backend rewriter)
 *   rows, placeholder, testId
 *   disabled: bool
 */
export function VoiceSmartNote({
  value,
  onChange,
  context = "note",
  rows = 3,
  placeholder = "Type or dictate…",
  testId = "voice-smart-note",
  disabled = false,
}) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing | rewriting | error
  const [error, setError] = useState("");
  const [beforeAiValue, setBeforeAiValue] = useState(null); // for "revert AI"
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    } catch {}
    streamRef.current = null;
    mediaRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    if (disabled) return;
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") {
      setError("Microphone not supported in this browser.");
      setState("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        stopStream();
        await handleTranscribe(blob);
      };
      mr.start();
      setState("recording");
    } catch (e) {
      setError(e?.message || "Microphone permission denied.");
      setState("error");
    }
  };

  const stopRecording = () => {
    try { mediaRef.current && mediaRef.current.stop(); } catch {}
  };

  const handleTranscribe = async (blob) => {
    setState("transcribing");
    setError("");
    try {
      const ext = (blob.type.includes("webm") ? "webm"
                 : blob.type.includes("wav")  ? "wav"
                 : blob.type.includes("mp4")  ? "mp4"
                 : "webm");
      const fd = new FormData();
      fd.append("file", blob, `clip.${ext}`);
      const { data } = await api.post("/ai/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const raw = (data?.text || "").trim();
      if (!raw) {
        setError("Nothing was transcribed — please try speaking again.");
        setState("error");
        return;
      }
      // Append transcript to any pre-existing text, then rewrite.
      const merged = (value ? value.trim() + "\n" : "") + raw;
      onChange(merged);
      await rewrite(merged);
    } catch (e) {
      setError(formatApiError(e) || "Transcription failed. Please retry.");
      setState("error");
    }
  };

  const rewrite = async (srcText) => {
    const src = (srcText ?? value ?? "").trim();
    if (!src) {
      setError("Nothing to polish — please type or dictate first.");
      setState("error");
      return;
    }
    setState("rewriting");
    setError("");
    setBeforeAiValue(value);
    try {
      const { data } = await api.post("/ai/voice/smart-notes", { text: src, context });
      const polished = (data?.text || "").trim();
      if (!polished) {
        setError("AI returned an empty note — keeping your original text.");
        setState("error");
        return;
      }
      onChange(polished);
      setState("idle");
      toast.success("AI polished your note. Review and Save when ready.");
    } catch (e) {
      setError(formatApiError(e) || "AI rewrite failed. Your original text is preserved.");
      setState("error");
    }
  };

  const revert = () => {
    if (beforeAiValue !== null) {
      onChange(beforeAiValue);
      setBeforeAiValue(null);
      setState("idle");
    }
  };

  const busy = state === "recording" || state === "transcribing" || state === "rewriting";
  const canRewrite = !!(value || "").trim() && !busy;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled || busy}
          data-testid={`${testId}-input`}
          className="pr-20"
        />
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {state === "recording" ? (
            <button
              type="button"
              onClick={stopRecording}
              data-testid={`${testId}-stop-btn`}
              className="tap-lift inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-sm animate-pulse"
              title="Stop recording"
            >
              <MicOff className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={startRecording}
              disabled={busy || disabled}
              data-testid={`${testId}-mic-btn`}
              className="tap-lift inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50"
              title="Record voice note"
            >
              <Mic className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => rewrite()}
          disabled={!canRewrite || disabled}
          className="rounded-full"
          data-testid={`${testId}-polish-btn`}
        >
          {state === "rewriting"
            ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Polishing…</>
            : <><Sparkles className="mr-1 h-3.5 w-3.5" /> Polish with AI</>}
        </Button>
        {beforeAiValue !== null && state !== "rewriting" && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={revert}
            className="rounded-full text-muted-foreground"
            data-testid={`${testId}-revert-btn`}
          >
            <RotateCw className="mr-1 h-3.5 w-3.5" /> Revert
          </Button>
        )}
        <StatusPill state={state} testId={testId} />
      </div>

      {error && (
        <div data-testid={`${testId}-error`} className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
    </div>
  );
}

function StatusPill({ state, testId }) {
  if (state === "idle") return null;
  const map = {
    recording:    { text: "Listening…",       cls: "bg-red-500/15 text-red-600 border-red-500/30" },
    transcribing: { text: "Transcribing…",   cls: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
    rewriting:    { text: "AI polishing…",   cls: "bg-primary/15 text-primary border-primary/30" },
    error:        { text: "Failed — retry",   cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  };
  const it = map[state] || map.recording;
  return (
    <span data-testid={`${testId}-status-${state}`} className={`pill border text-xs ${it.cls}`}>
      {state !== "error" && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
      {it.text}
    </span>
  );
}

export default VoiceSmartNote;
